import { Outlet, useLocation, Link, useNavigate } from "react-router-dom";
import { Building, Users, FileText, Settings, CheckCircle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/components/auth-provider";
import { useEffect } from "react";
import { DashboardHeader } from "@/components/dashboard/dashboard-header";

export default function AdminLayout() {
  const location = useLocation();
  const { status, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if not authenticated
  useEffect(() => {
    if (status === "unauthenticated") {
      navigate("/auth/signin", { replace: true });
    }
  }, [status, navigate]);

  // Redirect if not admin/super-admin
  // Note: user.role comes from Firebase Custom Claims (secure, server-side only)
  useEffect(() => {
    if (status === "authenticated" && user) {
      console.log("Admin layout - user role (from custom claims):", user.role);

      const userRole = user.role;

      // Only allow admins and super-admins
      if (userRole !== "admin" && userRole !== "super-admin") {
        console.log("User role not allowed for admin portal:", userRole);
        if (userRole === "landlord") {
          console.log("Redirecting landlord to landlord portal");
          navigate("/landlord", { replace: true });
        } else if (userRole === "tenant") {
          console.log("Redirecting tenant to tenant portal");
          navigate("/tenant", { replace: true });
        } else {
          console.log("No appropriate role found, redirecting to home");
          navigate("/", { replace: true });
        }
      } else {
        console.log("Access granted to admin portal for role:", userRole);
      }
    }
  }, [status, user, navigate]);

  const pathname = location.pathname;

  const tabs = [
    {
      label: "Properties",
      href: "/admin/properties",
      icon: Building,
    },
    { label: "Approvals", href: "/admin/approvals", icon: CheckCircle },
    { label: "Users", href: "/admin/users", icon: Users },
    { label: "Leases", href: "/admin/leases", icon: FileText },
    { label: "Settings", href: "/admin/settings", icon: Settings },
  ];

  const activeTab =
    tabs.find((tab) => pathname === tab.href)?.label || "Properties";

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <DashboardHeader
        title="Admin Dashboard"
        subtitle="Manage properties, users, leases, and system settings"
      />

      {/* Navigation Tabs */}
      <Tabs value={activeTab} className="w-full">
        <TabsList>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger key={tab.href} value={tab.label} asChild>
                <Link to={tab.href} className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </Link>
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      {/* Content */}
      <div>
        <Outlet />
      </div>
    </div>
  );
}
